import { UserSettings } from '@xeetr/types/models'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useContext } from 'react'

import { ActiveTenantContext, ActiveUserContext } from '../../context/common/Context'
import { UserService } from '../../services/User.service'
import { useAuth } from '../useAuth'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'
import { invalidateUserQueries } from '@/lib/mutationHelpers'
import { queryClient } from '@/lib/queryClient'

type TenantScopedKeys = 'dashboardColumns' | 'customerColumns' | 'statistics' | 'calendar'

/**
 * Generic hook for updating user settings that are scoped per tenant.
 * Handles the common pattern of settings stored as { [tenantId]: value }
 *
 * @param settingKey - The key in UserSettings to update
 * @param errorMessage - Custom error message to display on failure
 * @param successMessage - Custom success message to display on success (optional)
 * @returns Object with update function and isUpdating state
 *
 * @example
 * ```tsx
 * const { update, isUpdating } = useUpdateUserSetting('statistics', 'Failed to save statistics', 'Settings saved')
 * update({ showTotalBookings: true, showPeople: false })
 * ```
 */
export const useUpdateUserSetting = <K extends TenantScopedKeys>(
  settingKey: K,
  errorMessage?: string,
  successMessage?: string
) => {
  const { getAccessTokenSilently } = useAuth()
  const { tenantId } = useContext(ActiveTenantContext)
  const { user } = useContext(ActiveUserContext)

  type SettingValue = NonNullable<UserSettings[K]>[string]

  const mutation = useMutation({
    mutationFn: async (value: SettingValue) => {
      const token = await getAccessTokenSilently()
      const userService = new UserService(token)

      return userService.patchUserSettings({
        [settingKey]: {
          [tenantId]: value,
        },
      } as Partial<UserSettings>)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['userSettings'], data)
      if (user) invalidateUserQueries(user.id)

      if (successMessage) {
        showToast({
          severity: 'success',
          detail: successMessage,
        })
      }
    },
    onError: (error) => {
      console.error(`Failed to update ${settingKey}:`, error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: errorMessage || `Fehler beim Speichern der ${settingKey}-Einstellungen.`,
      })
    },
  })

  const update = useCallback(
    (value: SettingValue) => {
      mutation.mutate(value)
    },
    [mutation]
  )

  return { update, isUpdating: mutation.isPending }
}
